import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, ArrowLeft, ShieldCheck, Loader2, LogOut, Plus, Pencil, Trash2, X,
  FileText, TrendingUp, CheckCircle2, Clock, DollarSign, Mail, ListChecks,
  Search, ChevronDown, ChevronRight, Copy, RefreshCw, UserPlus, Upload, Users,
} from "lucide-react";
import { API } from "../lib/utils";
import Logo from "../components/Logo";

const CASE_TYPES = ["Cryptocurrency", "Romance", "Investment Fraud", "Forex", "Recovery Scam", "Phishing", "Other"];
const STATUSES = ["Intake", "Under Review", "Documentation", "In Recovery", "Resolved", "Closed"];
const SORTS = [["date", "Date"], ["name", "Client name"], ["type", "Case type"], ["status", "Status"], ["reference", "Reference"]];

const statusColor = (s) => ({
  Intake: "bg-muted text-muted-foreground",
  "Under Review": "bg-accent/15 text-accent-foreground",
  Documentation: "bg-calm-light text-calm",
  "In Recovery": "bg-trust-light text-trust",
  Resolved: "bg-safe-light text-safe",
  Closed: "bg-muted text-muted-foreground",
}[s] || "bg-muted text-muted-foreground");

const money = (n, c) => `${c || "USD"} ${Number(n || 0).toLocaleString()}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

export default function PrivateFile() {
  const [token, setToken] = useState(() => sessionStorage.getItem("rl_token") || "");
  const [role, setRole] = useState(() => sessionStorage.getItem("rl_role") || "");
  const [clientName, setClientName] = useState(() => sessionStorage.getItem("rl_client_name") || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedName, setSelectedName] = useState("");

  const authed = Boolean(token && role);
  const client = useMemo(
    () => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    [token]
  );

  const login = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/portal/login`, { passcode: code.trim(), name: selectedName });
      setToken(data.token); setRole(data.role); setClientName(data.client_name || "");
      sessionStorage.setItem("rl_token", data.token);
      sessionStorage.setItem("rl_role", data.role);
      sessionStorage.setItem("rl_client_name", data.client_name || "");
      toast.success(`Access granted — ${data.role === "admin" ? "Administrator" : "Client"} view`);
      setCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid access code");
    } finally { setLoading(false); }
  };

  const logout = async () => {
    try { await client.post("/portal/logout"); } catch (e) {}
    setToken(""); setRole(""); setClientName(""); setSelectedName("");
    ["rl_token", "rl_role", "rl_client_name"].forEach((k) => sessionStorage.removeItem(k));
    toast.success("Signed out securely");
  };

  if (!authed) {
    if (!selectedName) return <ClientPicker onPick={setSelectedName} />;
    return <Gate code={code} setCode={setCode} loading={loading} login={login} onBack={() => setSelectedName("")} />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="portal-authed">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="container flex h-20 items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-4">
            <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest ${role === "admin" ? "bg-primary text-primary-foreground" : "bg-secondary/15 text-secondary"}`} data-testid="portal-role-badge">
              {role === "admin" ? "Administrator" : "Client Portal"}
            </span>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-foreground/70 hover:text-destructive" data-testid="portal-logout">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>
      {role === "admin" ? <AdminDashboard client={client} /> : <ClientView client={client} clientName={clientName} />}
    </div>
  );
}

function ClientPicker({ onPick }) {
  const [names, setNames] = useState(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let live = true;
    axios.get(`${API}/clients/names`)
      .then((r) => live && setNames(r.data.names || []))
      .catch(() => { if (live) { setError(true); setNames([]); } });
    return () => { live = false; };
  }, []);

  const filtered = useMemo(
    () => (names || []).filter((n) => n.toLowerCase().includes(query.trim().toLowerCase())),
    [names, query]
  );

  return (
    <div className="hero-mesh flex h-screen min-h-[560px] flex-col" data-testid="portal-picker">
      <div className="container py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-primary/70 hover:text-secondary" data-testid="picker-back-home">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
      </div>
      <div className="container flex min-h-0 flex-1 flex-col pb-6">
        <div className="calm-card mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <Users className="h-7 w-7" />
          </div>
          <div className="trust-badge mx-auto mb-4">Restricted Area</div>
          <h1 className="mb-2 font-display text-3xl font-bold text-primary">Private File Access</h1>
          <p className="mb-6 text-sm text-muted-foreground">Select your name to continue.</p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name"
              autoFocus
              className="field-input pl-11"
              data-testid="picker-search"
            />
          </div>
          <div className="mt-3 min-h-0 flex-1">
            {names === null ? (
              <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{error ? "Could not load names. Please try again." : "No names found."}</div>
            ) : (
              <ul className="h-full space-y-2 overflow-y-auto pr-1 text-left" data-testid="picker-list">
                {filtered.map((n) => (
                  <li key={n}>
                    <button
                      type="button"
                      onClick={() => onPick(n)}
                      className="flex w-full items-center justify-between rounded-lg border border-input bg-white px-4 py-3 text-sm font-semibold text-foreground transition-all hover:-translate-y-0.5 hover:border-secondary/50 hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="picker-name"
                    >
                      <span>{n}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" /> Secured area. Access attempts are logged.
          </p>
        </div>
      </div>
    </div>
  );
}

function Gate({ code, setCode, loading, login, onBack }) {
  return (
    <div className="hero-mesh flex min-h-screen flex-col" data-testid="portal-gate">
      <div className="container py-10">
        {onBack ? (
          <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-primary/70 hover:text-secondary" data-testid="gate-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : (
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-primary/70 hover:text-secondary" data-testid="gate-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        )}
      </div>
      <div className="container flex flex-1 items-center justify-center pb-24">
        <div className="w-full max-w-md">
          <div className="calm-card text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
              <Lock className="h-7 w-7" />
            </div>
            <div className="trust-badge mx-auto mb-4">Restricted Area</div>
            <h1 className="mb-2 font-display text-3xl font-bold text-primary">Private File Access</h1>
            <p className="mb-6 text-sm text-muted-foreground">Enter your access code to continue.</p>
            <form onSubmit={login} className="space-y-4">
              <input type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Access code" autoFocus
                className="field-input text-center text-lg tracking-[0.3em]" data-testid="gate-code-input" />
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="gate-submit">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Verifying...</> : "Continue"}
              </button>
            </form>
            <p className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> Secured area. Access attempts are logged.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- CLIENT VIEW ------------------------- */
function ClientView({ client, clientName }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const fetchCases = useCallback((q = "") => {
    client.get(`/cases`, { params: { search: q } })
      .then(({ data }) => { setCases(data); setSelected((s) => data.find((c) => c.id === s?.id) || data[0] || null); })
      .catch(() => toast.error("Could not load case files"))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { const t = setTimeout(() => fetchCases(search), 300); return () => clearTimeout(t); }, [search, fetchCases]);

  if (loading) return <Center><Loader2 className="h-8 w-8 animate-spin text-secondary" /></Center>;

  return (
    <div className="container py-12" data-testid="client-view">
      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-widest text-secondary" data-testid="client-greeting">Welcome back, {clientName || "Client"}</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-primary md:text-4xl">Your Private Case File</h1>
        <p className="mt-2 text-muted-foreground">A confidential overview of your recovery case{cases.length !== 1 ? "s" : ""} and progress.</p>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input className="field-input pl-10" placeholder="Search your cases by type, reference, status..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="client-search" />
      </div>

      {cases.length === 0 ? (
        <div className="calm-card text-center text-muted-foreground">{search ? "No cases match your search." : "No case files are available yet. Your recovery specialist will update this area shortly."}</div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="space-y-3 lg:col-span-1">
            {cases.map((c) => (
              <button key={c.id} onClick={() => setSelected(c)} data-testid={`client-case-${c.reference}`}
                className={`w-full rounded-2xl border p-4 text-left transition-all hover-lift ${selected?.id === c.id ? "border-secondary bg-white shadow-soft" : "border-border bg-white/70"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-muted-foreground">{c.reference}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusColor(c.status)}`}>{c.status}</span>
                </div>
                <p className="mt-2 font-semibold text-primary">{c.case_type}</p>
                <p className="text-xs text-muted-foreground">Opened {fmtDate(c.date_opened)}</p>
              </button>
            ))}
          </div>

          {selected && (
            <div className="lg:col-span-2">
              <div className="calm-card">
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs text-muted-foreground">{selected.reference}</p>
                    <h2 className="font-display text-2xl font-semibold">{selected.case_type} Case</h2>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest ${statusColor(selected.status)}`}>{selected.status}</span>
                </div>
                <div className="mb-6 grid gap-4 sm:grid-cols-3">
                  <Stat icon={DollarSign} label="Amount Lost" value={money(selected.amount_lost, selected.currency)} />
                  <Stat icon={TrendingUp} label="Recovered" value={money(selected.recovered_amount, selected.currency)} tone="safe" />
                  <Stat icon={Clock} label="Last Updated" value={fmtDate(selected.last_updated)} />
                </div>
                {selected.summary && (
                  <div className="mb-6 rounded-lg border border-secondary/10 bg-calm-light/40 p-4">
                    <h4 className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">Case Summary</h4>
                    <p className="text-sm text-muted-foreground">{selected.summary}</p>
                  </div>
                )}
                <h4 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-muted-foreground"><ListChecks className="h-4 w-4" /> Progress Timeline</h4>
                {selected.updates?.length ? (
                  <ol className="relative space-y-5 border-l border-border pl-6" data-testid="client-timeline">
                    {selected.updates.map((u) => (
                      <li key={u.id} className="relative">
                        <span className="absolute -left-[27px] flex h-4 w-4 items-center justify-center rounded-full bg-secondary" />
                        <p className="text-sm font-semibold text-primary">{u.title}</p>
                        {u.note && <p className="text-sm text-muted-foreground">{u.note}</p>}
                        <p className="mt-1 text-xs text-muted-foreground">{fmtDate(u.date)}</p>
                      </li>
                    ))}
                  </ol>
                ) : <p className="text-sm text-muted-foreground">No updates yet.</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------- ADMIN DASHBOARD ------------------------- */
const emptyCase = () => ({ reference: "", client_id: "", email: "", country: "", case_type: "Cryptocurrency", status: "Intake", amount_lost: 0, currency: "USD", payment_method: "", recovered_amount: 0, assigned_to: "", summary: "", date_opened: new Date().toISOString() });

function AdminDashboard({ client }) {
  const [tab, setTab] = useState("clients");
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");
  const [expanded, setExpanded] = useState({});
  const [editingClient, setEditingClient] = useState(null);
  const [editingCase, setEditingCase] = useState(null);
  const [timelineFor, setTimelineFor] = useState(null);
  const [editingTest, setEditingTest] = useState(null);
  const [showMocks, setShowMocks] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const loadCore = useCallback(async () => {
    try {
      const [cl, s] = await Promise.all([client.get("/clients"), client.get("/portal/stats")]);
      setClients(cl.data); setStats(s.data);
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  }, [client]);

  const loadCases = useCallback(async (q = search) => {
    try { const { data } = await client.get("/cases", { params: { search: q, sort, order } }); setCases(data); }
    catch { toast.error("Failed to load cases"); }
  }, [client, search, sort, order]);

  useEffect(() => { loadCore(); }, [loadCore]);
  useEffect(() => { const t = setTimeout(() => loadCases(), 250); return () => clearTimeout(t); }, [loadCases]);
  useEffect(() => { if (tab === "submissions") client.get("/contact/submissions").then(({ data }) => setSubs(data)).catch(() => {}); }, [tab, client]);
  useEffect(() => { if (tab === "testimonials") client.get("/testimonials/all").then(({ data }) => setTests(data)).catch(() => {}); }, [tab, client]);
  useEffect(() => {
    if (!confirmDel) return;
    const t = setTimeout(() => setConfirmDel(null), 6000);
    return () => clearTimeout(t);
  }, [confirmDel]);

  const { realGroups, mockGroups } = useMemo(() => {
    const byId = {};
    cases.forEach((c) => { (byId[c.client_id] = byId[c.client_id] || []).push(c); });
    const q = search.trim().toLowerCase();
    const withCases = clients.map((cl) => ({ ...cl, cases: byId[cl.id] || [] }));
    const sortKey = (g) => {
      if (sort === "name") return g.name.toLowerCase();
      const latest = g.cases.reduce((m, c) => (!m || c.date_opened > m.date_opened ? c : m), null);
      if (!latest) return null;
      if (sort === "date") return latest.date_opened;
      if (sort === "type") return (latest.case_type || "").toLowerCase();
      if (sort === "status") return (latest.status || "").toLowerCase();
      return (latest.reference || "").toLowerCase();
    };
    const cmp = (a, b) => {
      const va = sortKey(a), vb = sortKey(b);
      if (va === null && vb === null) return a.name.localeCompare(b.name);
      if (va === null) return 1;
      if (vb === null) return -1;
      const r = String(va).localeCompare(String(vb));
      return order === "desc" ? -r : r;
    };
    const realGroups = withCases.filter(
      (g) => !g.mock && (!search || g.cases.length > 0 || g.name.toLowerCase().includes(q))
    );
    const mockGroups = showMocks
      ? withCases.filter((g) => g.mock && (!search || g.name.toLowerCase().includes(q)))
      : [];
    return { realGroups: realGroups.sort(cmp), mockGroups: mockGroups.sort(cmp) };
  }, [clients, cases, search, showMocks, sort, order]);

  // client handlers
  const saveClient = async (form) => {
    try {
      if (form.id) {
        if (form.mock) await client.post(`/clients/${form.id}/convert`, form);
        else await client.put(`/clients/${form.id}`, form);
      } else {
        await client.post("/clients", form);
      }
      toast.success(form.mock ? "Placeholder converted to client" : form.id ? "Client updated" : "Client created");
      setEditingClient(null); await loadCore(); await loadCases();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const removeClient = async (id) => {
    try { await client.delete(`/clients/${id}`); toast.success("Client deleted"); await loadCore(); await loadCases(); }
    catch { toast.error("Delete failed"); }
  };
  const regenCode = async (id) => {
    try { const { data } = await client.post(`/clients/${id}/regenerate-code`); toast.success(`New code: ${data.access_code}`); await loadCore(); }
    catch { toast.error("Could not regenerate code"); }
  };

  // case handlers
  const saveCase = async (form) => {
    try {
      const payload = { ...form, amount_lost: Number(form.amount_lost), recovered_amount: Number(form.recovered_amount) };
      if (form.id) await client.put(`/cases/${form.id}`, payload);
      else await client.post("/cases", payload);
      toast.success(form.id ? "Case updated" : "Case created"); setEditingCase(null); await loadCases(); await loadCore();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const removeCase = async (id) => {
    try { await client.delete(`/cases/${id}`); toast.success("Case deleted"); await loadCases(); await loadCore(); }
    catch { toast.error("Delete failed"); }
  };
  const addUpdate = async (caseId, title, note) => {
    try { await client.post(`/cases/${caseId}/updates`, { title, note }); toast.success("Update added"); await loadCases(); setTimelineFor(null); }
    catch { toast.error("Could not add update"); }
  };

  // testimonial handlers
  const saveTest = async (form) => {
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (form.id) await client.put(`/testimonials/${form.id}`, payload);
      else await client.post("/testimonials", payload);
      toast.success("Saved"); setEditingTest(null);
      const { data } = await client.get("/testimonials/all"); setTests(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const removeTest = async (id) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    try { await client.delete(`/testimonials/${id}`); toast.success("Deleted"); const { data } = await client.get("/testimonials/all"); setTests(data); }
    catch { toast.error("Delete failed"); }
  };

  const copyCode = (c) => { navigator.clipboard?.writeText(c); toast.success("Access code copied"); };

  if (loading) return <Center><Loader2 className="h-8 w-8 animate-spin text-secondary" /></Center>;

  return (
    <div className="container py-12" data-testid="admin-dashboard">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary md:text-4xl">Case Management</h1>
          <p className="mt-2 text-muted-foreground">Manage clients, their private access codes, cases, and testimonials.</p>
        </div>
        {tab === "clients" && <button onClick={() => setEditingClient({ name: "", email: "", access_code: "" })} className="btn-primary" data-testid="admin-new-client"><UserPlus className="h-5 w-5" /> Add Client</button>}
        {tab === "testimonials" && <button onClick={() => setEditingTest({ name: "", quote: "", video_url: "", poster_url: "", order: tests.length + 1, published: true })} className="btn-primary" data-testid="admin-new-testimonial"><Plus className="h-5 w-5" /> Add Testimonial</button>}
      </div>

      {stats && (
        <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Clients" value={stats.total_clients} card />
          <Stat icon={FileText} label="Total Cases" value={stats.total_cases} card tone="trust" />
          <Stat icon={CheckCircle2} label="Resolved" value={stats.resolved} card tone="safe" />
          <Stat icon={DollarSign} label="Total Recovered" value={money(stats.total_recovered, "USD")} card tone="safe" />
        </div>
      )}

      <div className="mb-6 flex gap-3">
        {[["clients", "Clients & Cases"], ["testimonials", "Testimonials"], ["submissions", "Enquiries"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${tab === k ? "bg-primary text-primary-foreground" : "border border-border bg-white text-foreground/70 hover:border-secondary"}`}>{l}</button>
        ))}
      </div>

      {tab === "clients" && (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input className="field-input pl-10" placeholder="Search by client, type, reference, status..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-search" />
            </div>
            <select className="field-input w-auto" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="admin-sort">
              {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
            </select>
            <button onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))} className="rounded-lg border border-border bg-white px-4 py-3 text-sm font-semibold hover:border-secondary" data-testid="admin-order">
              {order === "desc" ? "↓ Desc" : "↑ Asc"}
            </button>
            <label className="flex cursor-pointer select-none items-center gap-2 rounded-lg border border-border bg-white px-4 py-3 text-sm font-semibold text-foreground/80 hover:border-secondary" data-testid="admin-mocks-toggle">
              <input type="checkbox" className="h-4 w-4 accent-secondary" checked={showMocks} onChange={(e) => setShowMocks(e.target.checked)} data-testid="admin-show-mocks" />
              Show mock names ({clients.filter((c) => c.mock).length})
            </label>
          </div>

          <div className="space-y-3" data-testid="admin-clients-list">
            {realGroups.map((g) => (
              <div key={g.id} className="overflow-hidden rounded-2xl border border-border bg-white shadow-soft" data-testid={`client-group-${g.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <button onClick={() => setExpanded((e) => ({ ...e, [g.id]: !e[g.id] }))} className="flex items-center gap-3 text-left" data-testid={`client-expand-${g.id}`}>
                    {expanded[g.id] ? <ChevronDown className="h-5 w-5 text-secondary" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                    <div>
                      <p className="font-display text-lg font-semibold text-primary">{g.name}</p>
                      <p className="text-xs text-muted-foreground">{g.email || "No email"} · {g.cases.length} case{g.cases.length !== 1 ? "s" : ""}</p>
                    </div>
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-1.5 font-mono text-sm font-bold text-primary" data-testid={`client-code-${g.id}`}>
                      {g.access_code}
                      <button onClick={() => copyCode(g.access_code)} title="Copy code" className="text-muted-foreground hover:text-secondary" data-testid={`client-copy-${g.id}`}><Copy className="h-3.5 w-3.5" /></button>
                      <button onClick={() => regenCode(g.id)} title="Generate new code" className="text-muted-foreground hover:text-secondary" data-testid={`client-regen-${g.id}`}><RefreshCw className="h-3.5 w-3.5" /></button>
                    </span>
                    <IconBtn onClick={() => setEditingCase({ ...emptyCase(), client_id: g.id })} title="Add case" testid={`client-add-case-${g.id}`}><Plus className="h-4 w-4" /></IconBtn>
                    <IconBtn onClick={() => setEditingClient(g)} title="Edit client" testid={`client-edit-${g.id}`}><Pencil className="h-4 w-4" /></IconBtn>
                    <DeleteBtn active={confirmDel?.kind === "client" && confirmDel?.id === g.id} onAsk={() => setConfirmDel({ kind: "client", id: g.id })}
                      onConfirm={() => { setConfirmDel(null); removeClient(g.id); }}
                      askTestid={`client-delete-${g.id}`} confirmTestid={`client-delete-confirm-${g.id}`} title="Delete client" />
                  </div>
                </div>

                {expanded[g.id] && (
                  <div className="border-t border-border bg-muted/10 p-4" data-testid={`client-cases-${g.id}`}>
                    {g.cases.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">No cases for this client yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {g.cases.map((c) => (
                          <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white p-3" data-testid={`admin-case-row-${c.reference}`}>
                            <div className="flex flex-wrap items-center gap-3">
                              <span className="font-mono text-xs text-muted-foreground">{c.reference}</span>
                              <span className="font-semibold">{c.case_type}</span>
                              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${statusColor(c.status)}`}>{c.status}</span>
                              <span className="text-xs text-muted-foreground">Opened {fmtDate(c.date_opened)}</span>
                              <span className="text-xs text-muted-foreground">Lost {money(c.amount_lost, c.currency)} · Recovered <span className="text-safe">{money(c.recovered_amount, c.currency)}</span></span>
                            </div>
                            <div className="flex gap-1.5">
                              <IconBtn onClick={() => setTimelineFor(c)} title="Add update" testid={`admin-timeline-${c.reference}`}><ListChecks className="h-4 w-4" /></IconBtn>
                              <IconBtn onClick={() => setEditingCase(c)} title="Edit" testid={`admin-edit-${c.reference}`}><Pencil className="h-4 w-4" /></IconBtn>
                              <DeleteBtn active={confirmDel?.kind === "case" && confirmDel?.id === c.id} onAsk={() => setConfirmDel({ kind: "case", id: c.id })}
                              onConfirm={() => { setConfirmDel(null); removeCase(c.id); }}
                              askTestid={`admin-delete-${c.reference}`} confirmTestid={`admin-delete-confirm-${c.reference}`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {mockGroups.length > 0 && (
              <div data-testid="admin-mocks-section">
                <p className="mb-2 mt-6 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Placeholder names · no code, no data ({mockGroups.length})
                </p>
                <div className="space-y-2">
                  {mockGroups.map((g) => (
                    <div key={g.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-border bg-white/70 px-4 py-2.5" data-testid={`client-mock-${g.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-foreground/80">{g.name}</p>
                        <p className="text-xs text-muted-foreground">Placeholder · no access code</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setEditingClient(g)} className="rounded-lg bg-primary px-3.5 py-2 text-xs font-bold text-primary-foreground transition-opacity hover:opacity-90" data-testid={`client-convert-${g.id}`}>
                          Create profile
                        </button>
                        <DeleteBtn active={confirmDel?.kind === "client" && confirmDel?.id === g.id} onAsk={() => setConfirmDel({ kind: "client", id: g.id })}
                          onConfirm={() => { setConfirmDel(null); removeClient(g.id); }}
                          askTestid={`client-mock-delete-${g.id}`} confirmTestid={`client-mock-delete-confirm-${g.id}`} title="Remove placeholder" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {realGroups.length === 0 && mockGroups.length === 0 && <div className="calm-card text-center text-muted-foreground">No clients match your search.</div>}
          </div>
        </>
      )}

      {tab === "submissions" && (
        <div className="space-y-3" data-testid="admin-submissions">
          {subs.map((s) => (
            <div key={s.id} className="calm-card">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-semibold text-primary"><Mail className="h-4 w-4 text-secondary" /> {s.name}</div>
                <span className="text-xs text-muted-foreground">{fmtDate(s.created_at)}</span>
              </div>
              <p className="text-sm text-muted-foreground">{s.email}{s.phone ? ` · ${s.phone}` : ""}</p>
              <p className="mt-2 text-sm">{s.message}</p>
            </div>
          ))}
          {subs.length === 0 && <div className="calm-card text-center text-muted-foreground">No enquiries yet.</div>}
        </div>
      )}

      {tab === "testimonials" && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-testimonials">
          {tests.map((t) => (
            <div key={t.id} className="calm-card" data-testid={`admin-testimonial-${t.id}`}>
              <div className="relative mb-4 aspect-video overflow-hidden rounded-xl bg-muted">
                <img src={t.poster_url || "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=faces&fit=crop&w=800&h=450&q=80"} alt={t.name} className="h-full w-full object-cover" style={{ objectPosition: "center 30%" }} />
                {t.video_url && <span className="absolute right-2 top-2 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-bold uppercase text-primary-foreground">Video</span>}
                {!t.published && <span className="absolute left-2 top-2 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">Hidden</span>}
              </div>
              <p className="line-clamp-3 text-sm text-muted-foreground">{t.quote}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs font-medium text-trust">— {t.name}</span>
                <div className="flex gap-1.5">
                  <IconBtn onClick={() => setEditingTest(t)} title="Edit" testid={`admin-edit-testimonial-${t.id}`}><Pencil className="h-4 w-4" /></IconBtn>
                  <IconBtn onClick={() => removeTest(t.id)} title="Delete" danger testid={`admin-delete-testimonial-${t.id}`}><Trash2 className="h-4 w-4" /></IconBtn>
                </div>
              </div>
            </div>
          ))}
          {tests.length === 0 && <div className="calm-card text-center text-muted-foreground sm:col-span-2 lg:col-span-3">No testimonials yet.</div>}
        </div>
      )}

      {editingClient && <ClientModal initial={editingClient} client={client} onClose={() => setEditingClient(null)} onSave={saveClient} />}
      {editingCase && <CaseModal initial={editingCase} clients={clients} onClose={() => setEditingCase(null)} onSave={saveCase} />}
      {timelineFor && <TimelineModal caseItem={timelineFor} onClose={() => setTimelineFor(null)} onAdd={addUpdate} />}
      {editingTest && <TestimonialModal initial={editingTest} client={client} onClose={() => setEditingTest(null)} onSave={saveTest} />}
    </div>
  );
}

/* ------------------------- SHARED UI ------------------------- */
function Stat({ icon: Icon, label, value, tone, card }) {
  const toneCls = { safe: "text-safe bg-safe-light", trust: "text-trust bg-trust-light" }[tone] || "text-secondary bg-secondary/10";
  return (
    <div className={card ? "calm-card !p-5" : "rounded-xl border border-border bg-white p-4"}>
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${toneCls}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="font-display text-lg font-bold text-primary">{value}</p>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white transition-colors ${danger ? "hover:border-destructive hover:text-destructive" : "hover:border-secondary hover:text-secondary"}`}>{children}</button>
  );
}

function DeleteBtn({ active, onAsk, onConfirm, askTestid, confirmTestid, title = "Delete" }) {
  if (active)
    return (
      <button type="button" onClick={onConfirm} data-testid={confirmTestid}
        className="rounded-lg bg-destructive px-2.5 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90">
        Confirm delete?
      </button>
    );
  return <IconBtn onClick={onAsk} title={title} danger testid={askTestid}><Trash2 className="h-4 w-4" /></IconBtn>;
}

function Center({ children }) {
  return <div className="flex min-h-[60vh] items-center justify-center">{children}</div>;
}

function Field({ label, children }) {
  return <label className="block"><span className="mb-1.5 block text-sm font-semibold">{label}</span>{children}</label>;
}

function Modal({ children, title, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-primary/40 p-4 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 shadow-elevated" onClick={(e) => e.stopPropagation()}>
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-destructive" data-testid="modal-close"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ClientModal({ initial, client, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const generate = async () => {
    try { const { data } = await client.get("/clients/suggest-code"); setForm((f) => ({ ...f, access_code: data.access_code })); }
    catch { toast.error("Could not generate code"); }
  };
  return (
    <Modal onClose={onClose} title={form.mock ? "Create Client Profile" : form.id ? "Edit Client" : "Add Client"} testid="client-modal">
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
        <Field label="Client Full Name"><input className="field-input" value={form.name} onChange={set("name")} placeholder="e.g. Jane Smith" data-testid="client-name" required /></Field>
        <Field label="Email (optional)"><input className="field-input" value={form.email} onChange={set("email")} data-testid="client-email" /></Field>
        <Field label="Private Access Code">
          <div className="flex gap-2">
            <input className="field-input font-mono tracking-widest" value={form.access_code} onChange={set("access_code")} placeholder="Leave blank to auto-generate" data-testid="client-access-code" />
            <button type="button" onClick={generate} className="btn-teal shrink-0 !px-4 !py-2.5" data-testid="client-generate-code"><RefreshCw className="h-4 w-4" /> Generate</button>
          </div>
        </Field>
        <p className="-mt-2 text-xs text-muted-foreground">This is the unique code the client enters at /private-file to see only their own cases.</p>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-outline !py-2.5">Cancel</button>
          <button type="submit" className="btn-primary !py-2.5" data-testid="client-save">{form.mock ? "Create Profile" : "Save Client"}</button>
        </div>
      </form>
    </Modal>
  );
}

function CaseModal({ initial, clients, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Modal onClose={onClose} title={form.id ? "Edit Case File" : "New Case File"} testid="case-modal">
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Client">
            <select className="field-input" value={form.client_id} onChange={set("client_id")} data-testid="case-client" required>
              <option value="">Select a client…</option>
              {clients.filter((c) => !c.mock).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Reference"><input className="field-input" value={form.reference} onChange={set("reference")} placeholder="RL-2024-0001" data-testid="case-reference" required /></Field>
          <Field label="Date of Case">
            <input type="date" className="field-input" value={toDateInput(form.date_opened)} max="2100-12-31"
              onChange={(e) => setForm((f) => ({ ...f, date_opened: e.target.value ? new Date(e.target.value).toISOString() : f.date_opened }))}
              data-testid="case-date" />
          </Field>
          <Field label="Country"><input className="field-input" value={form.country} onChange={set("country")} data-testid="case-country" /></Field>
          <Field label="Case Type"><select className="field-input" value={form.case_type} onChange={set("case_type")} data-testid="case-type">{CASE_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Status"><select className="field-input" value={form.status} onChange={set("status")} data-testid="case-status">{STATUSES.map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Amount Lost"><input type="number" className="field-input" value={form.amount_lost} onChange={set("amount_lost")} data-testid="case-amount-lost" /></Field>
          <Field label="Recovered"><input type="number" className="field-input" value={form.recovered_amount} onChange={set("recovered_amount")} data-testid="case-recovered" /></Field>
          <Field label="Currency"><input className="field-input" value={form.currency} onChange={set("currency")} data-testid="case-currency" /></Field>
          <Field label="Payment Method"><input className="field-input" value={form.payment_method} onChange={set("payment_method")} data-testid="case-payment" /></Field>
          <Field label="Assigned To"><input className="field-input" value={form.assigned_to} onChange={set("assigned_to")} data-testid="case-assigned" /></Field>
        </div>
        <Field label="Summary"><textarea rows={3} className="field-input resize-none" value={form.summary} onChange={set("summary")} data-testid="case-summary" /></Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-outline !py-2.5">Cancel</button>
          <button type="submit" className="btn-primary !py-2.5" data-testid="case-save">Save Case</button>
        </div>
      </form>
    </Modal>
  );
}

function TimelineModal({ caseItem, onClose, onAdd }) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  return (
    <Modal onClose={onClose} title={`Add Update — ${caseItem.reference}`} testid="timeline-modal">
      <form onSubmit={(e) => { e.preventDefault(); if (title.trim()) onAdd(caseItem.id, title, note); }} className="space-y-4">
        <Field label="Update Title"><input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Bank dispute filed" data-testid="timeline-title" required /></Field>
        <Field label="Note"><textarea rows={3} className="field-input resize-none" value={note} onChange={(e) => setNote(e.target.value)} data-testid="timeline-note" /></Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-outline !py-2.5">Cancel</button>
          <button type="submit" className="btn-primary !py-2.5" data-testid="timeline-save">Add Update</button>
        </div>
      </form>
    </Modal>
  );
}

function TestimonialModal({ initial, client, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, video_url: data.url }));
      toast.success("Video uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <Modal onClose={onClose} title={form.id ? "Edit Testimonial" : "Add Testimonial"} testid="testimonial-modal">
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Client Name / Label"><input className="field-input" value={form.name} onChange={set("name")} placeholder="e.g. James R." data-testid="test-name" required /></Field>
          <Field label="Display Order"><input type="number" className="field-input" value={form.order} onChange={set("order")} data-testid="test-order" /></Field>
        </div>
        <Field label="Quote / Testimonial Text"><textarea rows={3} className="field-input resize-none" value={form.quote} onChange={set("quote")} placeholder="What the client said..." data-testid="test-quote" required /></Field>

        <div className="rounded-xl border border-dashed border-secondary/40 bg-calm-light/30 p-4">
          <p className="mb-2 text-sm font-semibold">Upload a video file</p>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-secondary px-4 py-2.5 text-sm font-semibold text-secondary-foreground hover:bg-secondary/90" data-testid="test-upload-label">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Choose .mp4 file"}
            <input type="file" accept="video/mp4,video/*" className="hidden" onChange={upload} disabled={uploading} data-testid="test-upload-input" />
          </label>
          {form.video_url && <p className="mt-2 flex items-center gap-1.5 text-xs text-safe" data-testid="test-video-set"><CheckCircle2 className="h-3.5 w-3.5" /> Video attached</p>}
          <p className="mt-2 text-xs text-muted-foreground">Uploaded straight to secure storage. Or paste an external URL below.</p>
        </div>

        <Field label="Video URL (auto-filled after upload, or paste one)"><input className="field-input" value={form.video_url} onChange={set("video_url")} placeholder="/api/files/... or https://.../video.mp4" data-testid="test-video-url" /></Field>
        <Field label="Poster / Thumbnail Image URL"><input className="field-input" value={form.poster_url} onChange={set("poster_url")} placeholder="https://.../poster.jpg" data-testid="test-poster-url" /></Field>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} data-testid="test-published" />
          Published (visible on homepage)
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-outline !py-2.5">Cancel</button>
          <button type="submit" disabled={uploading} className="btn-primary !py-2.5" data-testid="test-save">Save Testimonial</button>
        </div>
      </form>
    </Modal>
  );
}
